export interface IVisitExtraServiceMatch {
  id?: string | null;
  title: string;
  amount: number;
  unitPrice?: number | null;
  description?: string;
}

export interface IExtraItemVisitTarget {
  eventUid: string;
  amount: number;
  label: string;
}

export interface IExtraItemVisitSummary {
  targets: IExtraItemVisitTarget[];
  isAllVisits: boolean;
  summaryLabel: string;
}

/**
 * Returns the extra services that apply to a specific visit (eventUid or secondaryEventUid).
 */
export const getExtraServicesForVisit = (
  extraLineItems: any[] | undefined,
  eventUid: string,
  totalEventsCount: number,
  secondaryEventUid?: string,
): IVisitExtraServiceMatch[] => {
  if (!Array.isArray(extraLineItems) || extraLineItems.length === 0 || (!eventUid && !secondaryEventUid)) return [];

  const matches: IVisitExtraServiceMatch[] = [];

  for (const item of extraLineItems) {
    if (!item) continue;
    const title = String(item.title || "Extra Service").trim();

    // 1. Check allocations
    if (Array.isArray(item.allocations) && item.allocations.length > 0) {
      const alloc = item.allocations.find(
        (a: any) =>
          a &&
          ((eventUid && a.eventUid === eventUid) ||
            (secondaryEventUid && a.eventUid === secondaryEventUid)),
      );
      if (alloc) {
        const amount = typeof alloc.amount === "number" ? alloc.amount : Number.parseFloat(String(alloc.amount || 0));
        if (Number.isFinite(amount) && amount > 0) {
          matches.push({
            id: item.id ?? null,
            title,
            amount,
            unitPrice: alloc.unitPrice != null ? Number(alloc.unitPrice) : item.unitPrice != null ? Number(item.unitPrice) : undefined,
            description: item.description,
          });
        }
      }
    } else {
      // 2. Fallback: appliesTo or single visit invoice
      const appliesToUid = item.appliesTo?.eventUid;
      if (
        (eventUid && appliesToUid === eventUid) ||
        (secondaryEventUid && appliesToUid === secondaryEventUid) ||
        totalEventsCount === 1
      ) {
        const amount = typeof item.amount === "number" ? item.amount : Number.parseFloat(String(item.amount || 0));
        if (Number.isFinite(amount) && amount > 0) {
          matches.push({
            id: item.id ?? null,
            title,
            amount,
            unitPrice: item.unitPrice != null ? Number(item.unitPrice) : undefined,
            description: item.description,
          });
        }
      }
    }
  }

  return matches;
};

/**
 * Returns the total dollar amount of extra services that apply to a specific visit.
 */
export const getExtraServicesTotalForVisit = (
  extraLineItems: any[] | undefined,
  eventUid: string,
  totalEventsCount: number,
  secondaryEventUid?: string,
): number => {
  const matches = getExtraServicesForVisit(extraLineItems, eventUid, totalEventsCount, secondaryEventUid);
  return matches.reduce((sum, item) => sum + item.amount, 0);
};

/**
 * Returns which visits an extra line item applies to, with labels and a friendly summary.
 */
export const getVisitTargetsForExtraItem = (
  item: any,
  allEvents: any[],
): IExtraItemVisitSummary => {
  if (!item) return { targets: [], isAllVisits: false, summaryLabel: "" };

  const eventMap = new Map<string, { title: string; index: number }>();
  (allEvents || []).forEach((ev, idx) => {
    if (!ev) return;
    const dateStr =
      ev.startDate && !Number.isNaN(new Date(ev.startDate).getTime())
        ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(ev.startDate))
        : `Visit ${idx + 1}`;
    const info = { title: dateStr, index: idx + 1 };
    if (ev.uid) eventMap.set(ev.uid, info);
    if (ev.eventUid) eventMap.set(ev.eventUid, info);
  });

  const targets: IExtraItemVisitTarget[] = [];
  const totalEventsCount = allEvents?.length || 0;

  if (Array.isArray(item.allocations) && item.allocations.length > 0) {
    for (const alloc of item.allocations) {
      if (!alloc || !alloc.eventUid) continue;
      const amt = typeof alloc.amount === "number" ? alloc.amount : Number.parseFloat(String(alloc.amount || 0));
      const evInfo = eventMap.get(alloc.eventUid);
      const label = evInfo ? `Visit ${evInfo.index} (${evInfo.title})` : `Visit (${alloc.eventUid.slice(0, 8)})`;
      targets.push({
        eventUid: alloc.eventUid,
        amount: amt,
        label,
      });
    }
  } else if (item.appliesTo?.eventUid) {
    const evInfo = eventMap.get(item.appliesTo.eventUid);
    const amt = typeof item.amount === "number" ? item.amount : Number.parseFloat(String(item.amount || 0));
    const label = evInfo ? `Visit ${evInfo.index} (${evInfo.title})` : `Visit (${item.appliesTo.eventUid.slice(0, 8)})`;
    targets.push({
      eventUid: item.appliesTo.eventUid,
      amount: amt,
      label,
    });
  } else if (totalEventsCount === 1 && allEvents[0]?.uid) {
    const ev = allEvents[0];
    const evInfo = eventMap.get(ev.uid) || (ev.eventUid ? eventMap.get(ev.eventUid) : undefined);
    const amt = typeof item.amount === "number" ? item.amount : Number.parseFloat(String(item.amount || 0));
    targets.push({
      eventUid: ev.uid,
      amount: amt,
      label: evInfo ? `Visit 1 (${evInfo.title})` : "Visit 1",
    });
  }

  const isAllVisits = totalEventsCount > 1 && targets.length === totalEventsCount;
  let summaryLabel = "";

  if (isAllVisits) {
    summaryLabel = `All visits (${totalEventsCount})`;
  } else if (targets.length === 1 && totalEventsCount > 1) {
    summaryLabel = `Applies to: ${targets[0].label}`;
  } else if (targets.length > 0 && totalEventsCount > 1) {
    summaryLabel = `Applies to ${targets.length} of ${totalEventsCount} visits`;
  } else if (targets.length === 1 && totalEventsCount === 1) {
    summaryLabel = `Applies to: ${targets[0].label}`;
  } else {
    summaryLabel = "Entire invoice";
  }

  return { targets, isAllVisits, summaryLabel };
};
